import { Button, Spinner } from '@1trading/react';
import type { ColumnDef } from '@tanstack/react-table';
import { Link } from 'react-router-dom';
import { DataTable } from '@/components/data-table/data-table';
import { QueryErrorBanner } from '@/components/feedback/query-error';
import { StatusChip } from '@/components/ui/status-chip';
import type { BrokerBatchTransactionsResult } from '@/hooks/queries/brokers/use-broker-batch';
import { getErrorMessage } from '@/lib/api/error';
import type { WireBatchTransaction } from '@/services/api/brokers';
import { formatBrokerDateTime } from './batch-formatters';
import { transactionStatusVariant } from './batch-status';

// Module-level column defs — a stable reference so DataTable never sees "new columns"
const BATCH_TRANSACTION_COLUMNS: ColumnDef<WireBatchTransaction, unknown>[] = [
  {
    accessorKey: 'transaction_id',
    header: 'Transaction ID',
    cell: ({ row }) => <span className="font-mono text-xs">{row.original.transaction_id}</span>,
  },
  {
    accessorKey: 'client_account_id',
    header: 'Client Account',
    // UUID id-space; the v2 user console route is pending — emit the link anyway
    // for parity with the alerts table
    cell: ({ row }) => (
      <Link className="font-mono text-xs text-blue-400 hover:underline" to={`/operations/users/${row.original.client_account_id}`}>
        {row.original.client_account_id}
      </Link>
    ),
  },
  {
    accessorKey: 'amount',
    header: 'Amount',
    // Decimal STRING — render verbatim, never Number() it
    cell: ({ row }) => <span className="whitespace-nowrap tabular-nums">{row.original.amount}</span>,
  },
  { accessorKey: 'currency', header: 'Currency' },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => (
      <StatusChip className="px-2 py-0.5 text-xs" variant={transactionStatusVariant(row.original.status)}>
        {row.original.status}
      </StatusChip>
    ),
  },
  { accessorKey: 'type', header: 'Type' },
  {
    accessorKey: 'created_at',
    header: 'Created',
    cell: ({ row }) => <span className="whitespace-nowrap tabular-nums">{formatBrokerDateTime(row.original.created_at)}</span>,
  },
];

interface BatchTransactionsSectionProps {
  /** Owned by the page so it mounts in parallel with the batch summary */
  query: BrokerBatchTransactionsResult;
}

/** Cursor-paginated transactions list with Load More. */
export const BatchTransactionsSection = ({ query }: BatchTransactionsSectionProps) => {
  const hasRows = (query.data?.length ?? 0) > 0;

  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-base font-semibold">Transactions</h2>

      {query.isError && !hasRows ? (
        <QueryErrorBanner error={query.error} onRetry={() => query.refetch()} title="Failed to fetch batch transactions" />
      ) : (
        <>
          <DataTable
            columns={BATCH_TRANSACTION_COLUMNS}
            data={query.data}
            emptyMessage="No transactions found in this batch."
            getRowId={(row) => row.transaction_id}
            isLoading={query.isPending}
          />
          {/* A failed refetch/next-page keeps the loaded rows on screen; the error is surfaced below the table */}
          {query.isError && hasRows ? <QueryErrorBanner message={`Failed to fetch batch transactions: ${getErrorMessage(query.error)}`} onRetry={() => query.refetch()} /> : null}
          {query.hasNextPage ? (
            <div className="flex justify-center">
              <Button className="gap-2" disabled={query.isFetchingNextPage} onClick={() => query.fetchNextPage()} type="button" variant="outline">
                {query.isFetchingNextPage ? <Spinner className="size-4" /> : null}
                Load More
              </Button>
            </div>
          ) : null}
        </>
      )}
    </section>
  );
};
BatchTransactionsSection.displayName = 'BatchTransactionsSection';
